import {useEffect} from 'react';

import {useLocation, useNavigationType, createRoutesFromChildren, matchRoutes} from 'react-router-dom';
import * as Sentry from '@ergeon/erg-utils-js/exports/sentry';
import {setTags} from '@sentry/react';
import type {ErrorEvent} from '@sentry/core';
import type {User} from '@ergeon/erg-auth';

import {SENTRY_DSN} from '../constants';
import {getPackageVersionTags} from './packageVersions';

export {captureException} from '@ergeon/erg-utils-js/exports/sentry';

// Attaches the authenticated user to every Sentry event, so issues show
// how many (and which) users are affected. Clears the user on sign-out.
export const setSentryUser = (user?: User) => {
  Sentry.setUser(
    user
      ? {id: user.gid, email: user.email, username: user.full_name, roles: user.roles}
      : null,
  );
};

export const sentryIgnoreErrors: Array<string | RegExp> = [
  /\[Tawk\//,
  /WKWebView API client did not respond/,
  /Permissions check failed/,
  /NotAllowedError.*Clipboard.*Document is not focused/,
  /ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/,
  /Non-Error promise rejection captured with value: undefined/,
  /THREE\.WebGLProgram: Shader Error/,
  /Cannot read properties of undefined \(reading 'getBounds'\)/,
];

// Groups every "Failed to load catalog…" variant (by identity / zipcode / priceListId / cache)
// into a single Sentry issue instead of one issue per priceListId/zipcode.
const getCatalogFingerprint = (msg: string): string[] | null =>
  ((/^Failed to load catalog\b/).test(msg) ? ['catalog-load-failure'] : null);

const getFirstException = (event: ErrorEvent) => event.exception?.values?.[0];

const getEventMessage = (event: ErrorEvent): string =>
  getFirstException(event)?.value ?? event.message ?? '';

const getFrames = (event: ErrorEvent) => getFirstException(event)?.stacktrace?.frames;

// Sentry orders frames oldest-first, so the last element is the most-recently-called
// frame — i.e. the actual top of the call stack where the error was thrown.
const getTopFrame = (event: ErrorEvent): string => {
  const frames = getFrames(event);
  const last = frames?.[frames.length - 1];
  return last?.function ?? 'unknown';
};

const getShapeNotSetFingerprint = (event: ErrorEvent): string[] | null => {
  if (getEventMessage(event) !== 'Shape is not set') return null;
  return ['shape-not-set', getTopFrame(event)];
};

export const sentryBeforeSend = (event: ErrorEvent): ErrorEvent | null => {
  const msg = getEventMessage(event);
  const fingerprint = getCatalogFingerprint(msg) ?? getShapeNotSetFingerprint(event);
  if (fingerprint) event.fingerprint = fingerprint;
  return event;
};

export const init = () => {
  if (import.meta.env.MODE !== 'development') {
    Sentry.init({
      beforeSend: sentryBeforeSend,
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE,
      ignoreErrors: sentryIgnoreErrors,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      release: import.meta.env.SENTRY_RELEASE_NAME,
      tracesSampleRate: 0.05,
    });
    setTags(getPackageVersionTags());
  }
};
